import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Calendar as CalendarIcon, AlertCircle, Loader2 } from 'lucide-react';

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { 
  AnnouncementRule,
  createRule,
  updateRule
} from "../../services/announcementRuleService";

type WeekdayType = {
  value: number;
  label: string;
};

type MonthType = {
  value: number;
  label: string;
};

interface AnnouncementRuleDialogProps {
  open: boolean;
  rule: AnnouncementRule | null;
  onClose: () => void;
  onSaved: () => void;
}

const AnnouncementRuleDialog = ({ open, rule, onClose, onSaved }: AnnouncementRuleDialogProps) => {
  const [formData, setFormData] = useState<Partial<AnnouncementRule>>({
    name: '',
    description: '',
    type: 'date_range',
    startDate: new Date() as Date | string,
    endDate: undefined as Date | string | undefined,
    startTime: '',
    endTime: '',
    daysOfWeek: [],
    monthDay: 1,
    month: 0,
    priority: 1,
    isActive: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (rule) {
      setFormData({
        ...rule,
        startDate: rule.startDate ? new Date(rule.startDate) : undefined,
        endDate: rule.endDate ? new Date(rule.endDate) : undefined
      });
    }
    
    // Dialog açıldığında hataları temizle
    setValidationErrors({});
    setSubmitError(null);
  }, [rule, open]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.name?.trim()) {
      errors.name = 'Kural adı gerekli';
    }

    if (!formData.startDate) {
      errors.startDate = 'Başlangıç tarihi gerekli';
    }
    
    if (formData.type === 'date_range' && formData.startDate && formData.endDate && 
        new Date(formData.startDate) > new Date(formData.endDate)) {
      errors.endDate = 'Bitiş tarihi başlangıç tarihinden önce olamaz';
    }
    
    if (formData.type === 'weekly_recurring' && (!formData.daysOfWeek || formData.daysOfWeek.length === 0)) {
      errors.daysOfWeek = 'En az bir gün seçilmelidir';
    }
    
    if (formData.startTime && formData.endTime) {
      if (formData.startTime >= formData.endTime) {
        errors.endTime = 'Bitiş saati başlangıç saatinden sonra olmalıdır';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (field: keyof AnnouncementRule, value: unknown) => {
    setFormData((prev: Partial<AnnouncementRule>) => ({
      ...prev, 
      [field]: value
    }));
    
    // Clear validation error for the field if exists
    if (validationErrors[field as string]) {
      setValidationErrors((prev: Record<string, string>) => {
        const newErrors = { ...prev };
        delete newErrors[field as string];
        return newErrors;
      });
    }
  };

  const handleDayToggle = (day: number) => {
    const currentDays = formData.daysOfWeek || [];
    const newDays = [...currentDays];
    const index = newDays.indexOf(day);
    
    if (index === -1) {
      newDays.push(day);
    } else {
      newDays.splice(index, 1);
    }
    
    handleChange('daysOfWeek', newDays);
    
    // daysOfWeek hata mesajını temizle
    if (validationErrors.daysOfWeek) {
      setValidationErrors((prev: Record<string, string>) => {
        const updatedErrors = {...prev};
        delete updatedErrors.daysOfWeek;
        return updatedErrors;
      });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Tarih ve saat bilgilerini birleştir
      let startDateObj = formData.startDate ? new Date(formData.startDate as Date) : new Date();
      let endDateObj = formData.endDate ? new Date(formData.endDate as Date) : undefined;
      
      // startTime varsa startDate ile birleştir
      if (formData.startTime) {
        const [hours, minutes] = formData.startTime.split(':').map(Number);
        startDateObj.setHours(hours, minutes, 0, 0);
      }
      
      // endTime varsa endDate ile birleştir
      if (endDateObj && formData.endTime) {
        const [hours, minutes] = formData.endTime.split(':').map(Number);
        endDateObj.setHours(hours, minutes, 0, 0);
      }
      
      // API'ye gönderilecek veriyi hazırla
      const ruleData: Partial<AnnouncementRule> = {
        ...formData,
        startDate: startDateObj,
        endDate: endDateObj
      };
      
      console.log('Gönderilen kural verileri:', ruleData);
      
      if (rule?._id) {
        await updateRule(rule._id, ruleData as AnnouncementRule);
      } else {
        await createRule(ruleData as AnnouncementRule);
      }
      
      onSaved();
      onClose();
    } catch (error) {
      console.error('Kural kaydedilirken hata oluştu:', error);
      setSubmitError('Kural kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const weekdays: WeekdayType[] = [
    { value: 1, label: 'Pazartesi' },
    { value: 2, label: 'Salı' },
    { value: 3, label: 'Çarşamba' },
    { value: 4, label: 'Perşembe' },
    { value: 5, label: 'Cuma' },
    { value: 6, label: 'Cumartesi' },
    { value: 0, label: 'Pazar' }
  ];

  const months: MonthType[] = [
    { value: 0, label: 'Ocak' },
    { value: 1, label: 'Şubat' },
    { value: 2, label: 'Mart' },
    { value: 3, label: 'Nisan' },
    { value: 4, label: 'Mayıs' },
    { value: 5, label: 'Haziran' },
    { value: 6, label: 'Temmuz' },
    { value: 7, label: 'Ağustos' },
    { value: 8, label: 'Eylül' },
    { value: 9, label: 'Ekim' },
    { value: 10, label: 'Kasım' },
    { value: 11, label: 'Aralık' }
  ];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen && !isSubmitting) onClose();
    }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogTitle className="text-xl font-semibold">
          Duyuru Kuralı {rule ? 'Düzenle' : 'Ekle'}
        </DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          Duyuru kuralları, belirli zaman aralıklarında yöneticilere gösterilecek duyuruları belirler.
        </DialogDescription>
        
        {submitError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 col-span-1 md:col-span-2">
              <Label htmlFor="name" className="block text-sm font-medium">Kural Adı *</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={isSubmitting}
                className={cn(
                  validationErrors.name ? "border-red-500 ring-1 ring-red-500" : ""
                )}
                placeholder="Kural adını giriniz"
              />
              {validationErrors.name && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
              )}
            </div>

            <div className="col-span-1 md:col-span-2 space-y-2">
              <Label htmlFor="description" className="block text-sm font-medium">Açıklama</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                disabled={isSubmitting}
                className={cn(
                  validationErrors.description ? "border-red-500" : ""
                )}
              />
            </div>

            <div className="space-y-3">
              <Label className="text-base">Kural Türü</Label>
              <RadioGroup 
                value={formData.type || 'date_range'} 
                onValueChange={(value) => handleChange('type', value)}
                disabled={isSubmitting}
                className="flex flex-col space-y-2"  
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="date_range" id="date_range" />
                  <Label htmlFor="date_range" className="cursor-pointer">Tarih Aralığı</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="weekly_recurring" id="weekly_recurring" />
                  <Label htmlFor="weekly_recurring" className="cursor-pointer">Haftalık Tekrar</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="annual_recurring" id="annual_recurring" />
                  <Label htmlFor="annual_recurring" className="cursor-pointer">Yıllık Tekrar</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        </div>

        {/* Haftalık Tekrar Seçenekleri */}
        {formData.type === 'weekly_recurring' && (
          <div className="mt-6 border-t pt-6">
            <h3 className="text-base font-medium mb-2">Haftalık Tekrar Ayarları</h3>
            <Label className="mb-2 block text-sm">Haftanın Günleri *</Label>
            <div className="flex flex-wrap gap-2">
              {weekdays.map((day) => {
                const isSelected = formData.daysOfWeek?.includes(day.value);
                return (
                  <Button
                    key={day.value}
                    type="button"
                    variant={isSelected ? "default" : "outline"}
                    onClick={() => handleDayToggle(day.value)}
                    className={cn(
                      "min-w-[80px] transition-colors",
                      isSelected ? "bg-primary hover:bg-primary/90" : ""
                    )}
                    disabled={isSubmitting}
                  >
                    {day.label}
                  </Button>
                );
              })}
            </div>
            {validationErrors.daysOfWeek && (
              <p className="text-red-500 text-sm mt-2">{validationErrors.daysOfWeek}</p>
            )}
          </div>
        )}
        
        {/* Yıllık Tekrar Seçenekleri */}
        {formData.type === 'annual_recurring' && (
          <div className="mt-6 border-t pt-6">
            <h3 className="text-base font-medium mb-2">Yıllık Tekrar Ayarları</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div className="space-y-2">
                <Label htmlFor="monthDay" className="block text-sm font-medium">Gün</Label>
                <Select
                  value={formData.monthDay?.toString() || '1'}
                  onValueChange={(value) => handleChange('monthDay', parseInt(value))}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Gün seçin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <SelectItem key={day} value={day.toString()}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="month" className="block text-sm font-medium">Ay</Label>
                <Select
                  value={formData.month?.toString() || '0'}
                  onValueChange={(value) => handleChange('month', parseInt(value))}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Ay seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month.value} value={month.value.toString()}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
        
        {/* Tarih Aralığı Ayarları */}
        {formData.type === 'date_range' && (
          <div className="mt-6 border-t pt-6">
            <h3 className="text-base font-medium mb-4">Tarih Aralığı</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="startDate" className="block text-sm font-medium">Başlangıç Tarihi *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      id="startDate"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.startDate && "text-muted-foreground",
                        validationErrors.startDate ? "border-red-500 ring-1 ring-red-500" : ""
                      )}
                      disabled={isSubmitting}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.startDate ? (
                        format(new Date(formData.startDate), "d MMMM yyyy", { locale: tr })
                      ) : (
                        <span>Tarih seçin</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.startDate ? new Date(formData.startDate) : undefined}
                      onSelect={(date) => handleChange('startDate', date || new Date())}
                      locale={tr}
                      className="rounded-md border shadow" 
                    />
                  </PopoverContent>
                </Popover>
                {validationErrors.startDate && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.startDate}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="endDate" className="block text-sm font-medium">Bitiş Tarihi (opsiyonel)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      id="endDate"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.endDate && "text-muted-foreground",
                        validationErrors.endDate ? "border-red-500 ring-1 ring-red-500" : ""
                      )}
                      disabled={isSubmitting}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.endDate ? (
                        format(new Date(formData.endDate), "d MMMM yyyy", { locale: tr })
                      ) : (
                        <span>Tarih seçin</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.endDate ? new Date(formData.endDate) : undefined}
                      onSelect={(date) => handleChange('endDate', date)}
                      locale={tr}
                      fromDate={formData.startDate ? new Date(formData.startDate) : undefined}
                      className="rounded-md border shadow"
                    />
                  </PopoverContent>
                </Popover>
                {validationErrors.endDate && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.endDate}</p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Saat Aralığı Ayarları */}
        <div className="mt-6 border-t pt-6">
          <h3 className="text-base font-medium mb-2">Saat Aralığı (Opsiyonel)</h3>
          <p className="text-sm text-muted-foreground mb-4">Boş bırakılırsa, kural tüm gün geçerli olacaktır.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="startTime" className="block text-sm font-medium">Başlangıç Saati</Label>
              <Input
                id="startTime"
                type="time"
                value={formData.startTime || ''}
                onChange={(e) => handleChange('startTime', e.target.value)}
                disabled={isSubmitting}
                className={cn(
                  "pl-3",
                  validationErrors.startTime ? "border-red-500 ring-1 ring-red-500" : ""
                )}
              />
              {validationErrors.startTime && (
                <p className="text-sm text-red-500 mt-1">{validationErrors.startTime}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="endTime" className="block text-sm font-medium">Bitiş Saati</Label>
              <Input
                id="endTime"
                type="time"
                value={formData.endTime || ''}
                onChange={(e) => handleChange('endTime', e.target.value)}
                disabled={isSubmitting}
                className={cn(
                  "pl-3",
                  validationErrors.endTime ? "border-red-500 ring-1 ring-red-500" : ""
                )}
              />
              {validationErrors.endTime && (
                <p className="text-sm text-red-500 mt-1">{validationErrors.endTime}</p>
              )}
            </div>
          </div>
        </div>

        {/* Öncelik ve Aktiflik Ayarları */}
        <div className="mt-6 border-t pt-6">
          <h3 className="text-base font-medium mb-4">Diğer Ayarlar</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="priority" className="block text-sm font-medium">Öncelik</Label>
              <Select
                value={formData.priority?.toString() || '1'}
                onValueChange={(value) => handleChange('priority', parseInt(value))}
                disabled={isSubmitting}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Öncelik seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Düşük</SelectItem>
                  <SelectItem value="2">Orta</SelectItem>
                  <SelectItem value="3">Yüksek</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="isActive" className="block text-sm font-medium">Durum</Label>
              <div className="flex items-center space-x-2 pt-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive || false}
                  onCheckedChange={(checked) => handleChange('isActive', checked)}
                  disabled={isSubmitting}
                />
                <Label htmlFor="isActive" className="text-sm">
                  {formData.isActive ? 'Aktif' : 'Pasif'}
                </Label>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6 pt-6 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            İptal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {rule ? 'Güncelle' : 'Kaydet'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnnouncementRuleDialog;